import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { Color, NOTE_LIST_ROWS, Size, noteIsRich, noteListCap, sanitizeNoteText, t, tileItemTypes, type Attending, type AvatarPerson, type IconMeaning, type CertificateCard as CertificateCardFacts, type JobTimelineTitle, type StatusResolver, type TaxExemptBadge as TaxExemptBadgeFacts, type TileBounds } from '@bms2/shared';
import { Button, Checkbox, Pill, RichTextView, StatusPill } from '../controls';
import { Icon } from '../Icon';
import { MoneyText } from '../Money';
import { Text } from '../Text';
import { AttendeeAvatar } from './AttendeeAvatar';
import { CertificateCard } from './CertificateCard';
import { FileRow, type FileRowButton, type FileRowScan, type FileRowVerified } from './FileRow';
import { NoteThread, type NoteThreadProps } from './NoteThread';
import type { RowMenuEntry } from './RowMenu';
import { TaxExemptBadge } from './TaxExemptBadge';
import styles from './Tile.module.css';
import { useStatusResolver } from '../../shell/StatusResolver';
import { useTileListRows } from '../../shell/ViewerZone';
import { TILE_FOLD_KEY_PREFIX, devicePreferenceStore, readPreference, writePreference } from '../../shell/devicePreference';

/**
 * A detail line: the parts the server joined, each unbreakable.
 *
 * 🔨 A line breaks between its parts and never inside one. 💰 A phone number
 * wrapped after its area code reads as two numbers (product owner,
 * 2026-09-14).
 */
const DETAIL_SEPARATOR = ' · ';

function Detail({ text, onePerLine = false }: { text: string; onePerLine?: boolean }) {
  return (
    <span className={styles.itemDetail}>
      {text.split(DETAIL_SEPARATOR).map((part, index) => (
        <Fragment key={`${part}-${index}`}>
          {index > 0 && !onePerLine ? DETAIL_SEPARATOR : null}
          <span className={styles.detailPart}>{part}</span>
        </Fragment>
      ))}
    </span>
  );
}

/**
 * Tile (piece 4): a header of title and optional Add button over a
 * bounded, internally-scrolling item list, with ten item-type renderers.
 * Every Tile declares its own minWidth and maxWidth, which the canvas reads
 * and never violates; the Tile stops growing at its maximum on its own.
 */
export const tileDocumentedStates = tileItemTypes;

/** A small status-coloured pill beside a row: a file's Verified, a site's role, an employment's Current. */
export interface TilePill {
  text: string;
  group: 'good' | 'warning' | 'error' | 'inactive';
}

/**
 * A row's own reach, drawn as an outlined glyph beside its controls: the maps
 * search an address opens, the message an address takes, the call a line takes.
 * 🔨 It is not a row action and it comes from no registry — it is derived from
 * the value the row already carries, the way a row's route is. A row whose
 * value names nowhere to go carries none.
 */
export interface TileRowLink {
  href: string;
  label: string;
  icon: IconMeaning;
  /** A place outside the application opens in its own tab; a mailto: or tel: hands over to the device in place. */
  newTab?: boolean;
  /**
   * 🔒 The word the reach draws beside its glyph, where it is the row's own
   * action rather than a glyph beside a value (decision 0623): a person row's
   * Call and Email read as the contact row's do, so one standard answers both.
   * A reach with no word stays the outlined glyph it has always been.
   */
  text?: string;
  /** A dialable line's Call is outlined in green; every other worded reach is outlined in grey (decisions 0623, 1329). */
  tone?: 'call' | 'email' | 'web';
  /**
   * 🔒 Every line the reach can take, the one its button takes first, where the
   * row holds more than one (T214; decision 0692). The button carries a chevron
   * that opens them in the row; one line or none draws the plain button.
   */
  choices?: readonly TileRowChoice[];
}

/** One line behind a reach's chevron: where it goes, its name — the kind, its own word, its primary mark — and the value as drawn. */
export interface TileRowChoice {
  href: string;
  name: string;
  value: string;
}

/** One job on the Employments timeline: the company, its days, whether it runs, the titles held, and the controls of its newest position. */
export interface JobTimelineEntry {
  key: string;
  company: string;
  route?: string | null;
  span: string;
  running: boolean;
  pill?: TilePill | null;
  titles: readonly JobTimelineTitle[];
  button?: { label: string; onAct: () => void; icon?: IconMeaning } | null;
  onEdit?: (() => void) | null;
  editLabel?: string;
  onDelete?: (() => void) | null;
  deleteLabel?: string;
}

export type TileItem =
  | { type: 'statusOnly'; label: string; entityType: string; statusValue: string }
  | { type: 'labelValueGrid'; pairs: readonly { label: string; value: string; /** 🔒 The value is a formatted amount; only then is it scanned for a negative figure (decision 2155). */ money?: boolean; /** 🔒 The pair's status beside its value, such as the Account standing's Over the limit (decision 3280). */ pill?: TilePill | null; route?: string | null; link?: TileRowLink | null; button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string; /** 🔒 Delete this link, the holder's correction of a link made by mistake (T728; decision 1500). */ onDelete?: (() => void) | null; deleteLabel?: string }[] }
  /** The Note item: author and date on one line, an optional flag pill, then the text (figure 35). */
  | { type: 'note'; text: string; /** The note's own id, as a handle for a suite and a person's browser tools; drawn nowhere a person reads. */ id?: string | null; issue?: boolean; author?: string | null; when?: string | null; pill?: TilePill | null; onEdit?: (() => void) | null; editLabel?: string; /** 🔒 The replies beneath the note and its Reply box, where the page carries them (decision 0671). */ thread?: NoteThreadProps | null; /** 🔒 The Deal stage-card row the note was written on, as a small pill such as Intent · Need (decision 0725). */ section?: string | null; /** Attributes the row carries beside the tile's own, a list's handle on one note: the document notes panel's (decision 0951, as amended in batch N3). */ data?: Readonly<Record<string, string>>; /** Attributes the author's pencil carries beside the tile's own. */ editData?: Readonly<Record<string, string>> }
  /** The Person item: an Attendee Avatar beside stacked name and role (figure 40). */
  /** 🔒 A Person row carries the reach its own lines give it: a call on a number, a message on an address (product owner, 2026-09-20). */
  | {
      type: 'person'; person: AvatarPerson; attending: Attending; /** 🔒 The person's own id, the row's key so an open reach list stays with its person when the list reorders (T538); position is the fallback. */ id?: string | null; /** 🔒 T3028: a mark beside the name, the gold dot of a person with suggestions. */ marker?: ReactNode; detail?: string | null; route?: string | null; pill?: TilePill | null; links?: readonly TileRowLink[]; button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string; /** 🔒 Delete this link, the holder's correction of a link made by mistake (T728; decision 1500). */ onDelete?: (() => void) | null; deleteLabel?: string;
      /** 🔒 What stands under the name and its caption, where the list is outside a tile: the Deal's Authority row puts the role pill and the note there (decision 0719). */
      below?: ReactNode;
      /** Attributes the row's own item carries, a suite's handle on one person. */
      data?: Readonly<Record<string, string>>;
      /**
       * 🔒 Decision 11980: the Company's Contacts tile lays each contact as one row across the tile, never a card in a
       * column. The title stands on a line of its own under the name and ends in an ellipsis before the acts move, the
       * detail lines stand under it at the same indent, and the acts keep one column at the right that every row shares.
       */
      row?: boolean;
      /** 🔒 The title a row-form contact draws on its own line under the name (decision 11980); a stacked row folds it into its caption. */
      title?: string | null;
    }
  /**
   * The File item: a filename in a bordered row, its type as a tinted chip, and
   * a flags group at the right holding the restriction pill and then the file's
   * own status pill (figure 41; the owner's artifact, 2026-09-20). 🔒 It draws
   * through the one File row a document's Files block draws too (decision 3010).
   */
  | { type: 'file'; name: string; description?: string | null; version?: number | null; detail?: string | null; quarantine?: string | null; /** 🔒 Decision 7420: the file's bytes are stored encrypted, drawn as a lock beside the name. */ encrypted?: boolean; route?: string | null; href?: string | null; mimeType?: string | null; /** 🔒 Decision 11301: the document's date as the person gave it, on the row's first line. */ dated?: string | null; scan?: FileRowScan | null; verified?: FileRowVerified | null; chip?: string | null; /** 🔒 The record the file reached this tile through, its own label beside the type (decision 6040). */ origin?: string | null; flag?: TilePill | null; pill?: TilePill | null; button?: FileRowButton | null; /** 🔒 A second labelled action after the first, as the full-width row draws its two: the Deal's Share beside Release (decision 6040). */ secondButton?: FileRowButton | null; onEdit?: (() => void) | null }
  /** The Location item: a bold name with a role Pill beside it, and the address beneath (figures 42, 43). */
  | { type: 'location'; name: string; address: string; detail?: string | null; route?: string | null; pill?: TilePill | null; /** 🔒 The gold certificate badges of a site that holds Tax Exempt certificates, beside its role pill (T3076; decision 7390). */ badges?: readonly TaxExemptBadgeFacts[] | null; link?: TileRowLink | null; button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string; /** 🔒 Delete this link on a site row, the holder's correction of a site made by mistake (decision 7380). */ onDelete?: (() => void) | null; deleteLabel?: string }
  /** The Full-width row: a name, an optional Tagged Record-style Button, and a value right-aligned (figure 44). */
  /**
   * A dated role at an organisation, the figure's Company Experience (piece 4;
   * decision 0367): the role in the row's own weight, the organisation and the
   * span beneath it, a Current pill at the right and a pencil beside it.
   * 🔨 It is not the Timeline. A Timeline answers *when did things happen*, and
   * leads with the date; this answers *what have they done*, and leads with the
   * role. The Employments tile drew as a Timeline and read as a log.
   */
  | { type: 'datedRole'; role: string; organization?: string | null; span?: string | null; route?: string | null; pill?: TilePill | null; button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string; /** 🔒 Delete this link, the holder's correction of a link made by mistake (T728; decision 1500). */ onDelete?: (() => void) | null; deleteLabel?: string; /** 🔒 A page outside the application the row names, such as a school's LinkedIn page, drawn as the row's reach (decision 6400). */ link?: TileRowLink | null; /** Attributes the row's own item carries, a suite's handle on one row (decision 6400). */ data?: Readonly<Record<string, string>> }
  | { type: 'fullWidthRow'; text: string; value?: string | null; /** 🔒 The value is a formatted amount (decision 2155). */ money?: boolean; /** The group this row opens, drawn as a heading above it; a row inside a group carries none (product owner, 2026-09-20). */ heading?: string | null; /** 🔨 An arrival and a departure, stacked one instant to a line so a span never breaks mid-range (product owner, 2026-09-13). */ when?: { from: string; until?: string | null } | null; /** The record the row names, where it names one: the row's text is then a link to it. */ route?: string | null; /** 🔒 A document the viewer cannot open (decision 10370, building 10240): drawn without its link, and a press shows the page's Permission denied banner. */ onRefused?: (() => void) | null;  pill?: TilePill | null; button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string; /** A row that reads muted, such as a NotApplicable supplier (decision 0337). */ muted?: boolean; /** 🔒 Delete this link, the holder's correction of a link made by mistake (T728; decision 1500). */ onDelete?: (() => void) | null; deleteLabel?: string }
  /** The Timeline item: a title, an inline status Pill when current, and a detail line beneath (figures 45, 46). */
  /**
   * 🔒 One Tax Exempt certificate as a fact card (T3082; decision 7440): the gold badge and the state in its header, the
   * label-and-value grid beneath, and the one ⋯ menu of Edit, View Certificate and End in its foot. The card's words
   * come from the shared `certificateCard`, so the phone draws the same one.
   */
  | { type: 'certificateCard'; card: CertificateCardFacts; menu: readonly RowMenuEntry[]; menuLabel: string; id?: string | null }
  /**
   * 🔒 A person's Employments as a timeline (T3097; decision 7480): one job per employment down a thin rule, a dot on it filled while the
   * job runs and hollow once it has ended, the company in bold as a link with its span, and the titles held there beneath it.
   */
  | { type: 'jobTimeline'; jobs: readonly JobTimelineEntry[] }
  | { type: 'timeline'; entries: readonly { when: string; text: string; detail?: string | null; pill?: TilePill | null; route?: string | null; /** The one action and the pencil an entry offers, from the registry (decision 0309): Revoke on a live agreement. */ button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string }[] }
  | { type: 'checklist'; entries: readonly { label: string; done: boolean }[] }
  /**
   * 🔒 The Document Row (decision 0591): a tile listing documents draws one grid
   * with four columns — the date, the kind of document, its status pill and its
   * name — so a Sales or Work tile reads down its columns the way History does.
   * 🔨 The date is the grid's first column and never also inside the row's text.
   */
  | { type: 'documentRow'; date: string; kind?: string | null; text: string; detail?: string | null; pill?: TilePill | null; route?: string | null; /** 🔒 A document the viewer cannot open (decision 10370, building 10240): drawn without its link, and a press shows the page's Permission denied banner. */ onRefused?: (() => void) | null; button?: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit?: (() => void) | null; editLabel?: string }
  /**
   * The tenth renderer, added with the dashboard: a row that names a place and
   * links to it, carrying a value where it has one. A work-queue count links out
   * to where the queue already lives (decision 0116), and none of the nine above
   * carries a route. A null value draws an em dash, which is not a zero.
   */
  | { type: 'linkRow'; label: string; route: string; value?: string | number | null; /** 🔒 The value is a formatted amount (decision 2155). */ money?: boolean; key?: string }
  /**
   * 🔒 The stacked contact row (decision 0623): the standard every phone and
   * email item draws through. The kind's glyph at the far left, vertically
   * centred on the text; the kind's human-English name bold over the job it is
   * held in, muted; the value in the middle; the one action the line offers at
   * the right. *"This shoudl be the standard on the app for the phone item"* —
   * product owner, 2026-09-22.
   * 🔨 The action is a link and never a button element, so the device's own
   * handler takes it: a phone dials, a mail client opens a draft. A fax and a
   * note carry none.
   */
  | { type: 'contactRow'; name: string; value: string; icon?: IconMeaning | null; caption?: string | null; qualifier?: string | null; action?: { label: string; href: string; tone: 'call' | 'email' | 'web'; newTab?: boolean } | null; onEdit?: (() => void) | null; editLabel?: string };

/**
 * 🔒 Whether a foldable tile is folded, remembered PER DEVICE and per tile
 * name, so folding History once leaves it folded on every record page
 * (decision 0500).
 *
 * 🔨 Per device rather than against the login, because it is a reading
 * preference on the screen in front of somebody, not a fact about them. A
 * browser that refuses to store reads as open, which is the safe way to be
 * wrong: a person meets the panel they asked to hide rather than losing a
 * panel they never hid.
 */
export function tileFolded(title: string, store = devicePreferenceStore()): boolean {
  return readPreference(`${TILE_FOLD_KEY_PREFIX}${title}`, (raw) => raw === 'folded', false, store);
}

export function rememberTileFolded(title: string, folded: boolean, store = devicePreferenceStore()): void {
  writePreference(`${TILE_FOLD_KEY_PREFIX}${title}`, folded ? 'folded' : 'open', store);
}

export interface TileProps extends TileBounds {
  /** 🔨 A tile drawn as a page rather than on a canvas passes `Infinity` as its maxWidth and takes the page's width: What's new (piece 12, the owner 2026-09-23). */
  title: string;
  items: readonly TileItem[];
  /** 🔒 A count beside the title, where the page states one: the Deal's Documents tile (decision 1217). */
  count?: number | null;
  /** The header's action, where the tile has one to give; the label is the tile's own ("↑ Upload", "+ Add Event"). */
  onAdd?: () => void;
  addLabel?: string;
  /**
   * 🔒 A second header button, drawn before the add action (decision 2851): the
   * Site page's certificates tile carries Exempt Customer Steps beside + Add
   * Certificate. It is the tile's own button and shows whether or not the add does.
   */
  secondAction?: { label: string; onAct: () => void };
  /** An action that is a place rather than a modal, such as + Hire opening the hire screen. */
  addRoute?: string;
  resolve?: StatusResolver;
  /**
   * 🔨 A Files tile takes a drop anywhere on its face, not only inside a
   * modal's zone: the files it is handed go to the upload modal, which asks
   * for the file type before any of them is sent (piece 4, figure 41).
   */
  onDropFiles?: (files: readonly File[]) => void;
  /** A construction of the page's own, drawn inside the tile's frame beside its items: the Deal Progress ladder and the StageCard (piece 19). */
  children?: ReactNode;
  /**
   * 🔒 The box a list is added to from, drawn at the top of the list under the
   * heading (decision 0910): a notes tile's composer, in place of a header
   * action. It folds with the tile and draws over an empty list as over a full
   * one, so the next list of notes gets it by passing one construction.
   */
  composer?: ReactNode;
  /** A construction drawn at the tile's right, beside the items rather than under them: the Item's image (piece 17; decision 0269). */
  aside?: ReactNode;
  /** A glyph before the title, where the tile stands for a category rather than a section: the Settings Index (decision 0289). A section tile draws no mark there (decision 1327). */
  icon?: IconMeaning;
  /**
   * 🔒 The tile folds to its own heading behind a chevron, and the choice is
   * remembered for every record page (decision 0500). Every record-page tile carries it (decision 6211); a folded tile keeps its count and its Add action.
   */
  collapsible?: boolean;
  /**
   * 🔒 One control of the page's own in the header, before the fold chevron (decision 8840): the Bank Summary's
   * range select. It stands whether the tile is folded or open, so a folded tile keeps its heading and its choice.
   */
  headerControl?: ReactNode;
  /**
   * 🔒 How many rows to draw before offering the rest (decision 0500): a Files
   * tile's ten, with the count stated. A tile that names none and whose items
   * are a list takes the Organization's Tile list rows, four by default
   * (decision 5960).
   */
  pageSize?: number;
  /**
   * 🔒 How many History entries stand before the list scrolls inside for the
   * rest (T588; decision 1404). Every row is drawn; the window only bounds the
   * list's height, and the tile then stands at its content's height.
   */
  windowRows?: number;
  /**
   * 🔒 The words of an empty record-page tile, None yet (decision 6700, amending 6211 for this tile): the
   * tile is its title row alone, the words muted on that row and its Add action kept. It draws no body and
   * no chevron, since one row has nothing to fold. A tile with a composer or a panel of its own is not empty.
   */
  empty?: string;
}

/**
 * 🔨 A row that names a route navigates inside the application, so the
 * shell's own state — the emulation among it — survives the click. 💰 A plain
 * anchor reloads the document and drops View as on the way (found while
 * building, 2026-09-09). Outside a router, as on a bare harness, the anchor
 * stands in.
 */
function RowLink({ route, className, children, ...rest }: { route: string; className?: string | undefined; children: ReactNode; title?: string; 'data-link-row'?: string }) {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return (
      <Link to={route} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={route} className={className} {...rest}>
      {children}
    </a>
  );
}

function pillOf(pill: TilePill | null | undefined): ReactNode {
  if (!pill) return null;
  return (
    <span className={styles.rowPill} data-tile-pill={pill.group}>
      <Pill text={pill.text} foreground={`var(--status-${pill.group}-text)`} background={`var(--status-${pill.group}-bg)`} size={Size.Small} />
    </span>
  );
}

/** A row's own body, wrapped in its link where it names a route. */
/**
 * 🔒 A row naming a document the viewer cannot open (decision 10370, building 10240): its words with no link, and a
 * press that asks the page for its Permission denied banner rather than opening a page that would refuse.
 */
function Refused({ onRefused, className, children }: { onRefused: () => void; className: string | undefined; children: ReactNode }) {
  return (
    <button type="button" className={`${className ?? ''} ${styles.refusedRow}`} onClick={onRefused} title={t('record.tile.refusedHint')} aria-description={t('record.tile.refusedHint')} data-tile-refused="true">
      {children}
    </button>
  );
}

function Linked({ route, onRefused, children }: { route?: string | null | undefined; onRefused?: (() => void) | null | undefined; children: ReactNode }) {
  if (!route && onRefused) return <Refused onRefused={onRefused} className={styles.rowLink}>{children}</Refused>;
  return route ? (
    <RowLink route={route} className={styles.rowLink}>
      {children}
    </RowLink>
  ) : (
    <span className={styles.rowLink}>{children}</span>
  );
}

/**
 * A note's free text is capped at three lines with an ellipsis, and a Show more
 * chevron appears only when the text was actually truncated; expanding one note
 * never touches another (specification 2.9, figures 35 and 36; piece 4).
 *
 * 🔒 The foot is one line (decision 5493): Show more where the text is cut and,
 * at the right, the replies glyph with its count, which opens the replies. The
 * glyph is drawn by the thread, so Show more is handed to it to stand in the
 * same line; a note with no thread draws Show more alone. 🔨 A note that is not
 * cut draws the glyph at the end of its own last line instead, wrapping to the
 * line beneath when that line is full (T2904).
 */
function NoteText({ text, issue, thread }: { text: string; issue: boolean; thread?: NoteThreadProps | null | undefined }) {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const expandedRef = useRef(false);
  const [slot, setSlot] = useState<HTMLSpanElement | null>(null);
  // The measurement is taken clamped, so the chevron follows the width the note actually has;
  // an uncut note draws inline for the bubble, so the inline mark is lifted while it is measured.
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || expandedRef.current) return;
    const inline = el.getAttribute('data-note-inline');
    el.removeAttribute('data-note-inline');
    setTruncated(el.scrollHeight > el.clientHeight + 1);
    if (inline !== null) el.setAttribute('data-note-inline', inline);
  }, []);
  useLayoutEffect(() => {
    expandedRef.current = expanded;
    measure();
  }, [text, expanded, measure]);
  // 🔨 T2911: a width change and a web font arriving late move the line breaks, so both measure again.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let cancelled = false;
    let watcher: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      let width = el.clientWidth;
      watcher = new ResizeObserver(() => {
        if (el.clientWidth === width) return;
        width = el.clientWidth;
        measure();
      });
      watcher.observe(el);
    }
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(() => { if (!cancelled) measure(); });
    }
    return () => {
      cancelled = true;
      watcher?.disconnect();
    };
  }, [text, measure]);
  const more = truncated ? (
    <button type="button" className={styles.noteMore} onClick={() => setExpanded((was) => !was)} aria-expanded={expanded} data-note-more={expanded ? 'less' : 'more'}>
      <Icon meaning={expanded ? 'collapse' : 'expand'} size="inline" />
      {t(expanded ? 'note.showLess' : 'note.showMore')}
    </button>
  ) : null;
  // 🔨 T2904: the bubble stands at the end of the text's last line unless the note is cut, where the foot keeps it.
  const inlineBubble = Boolean(thread) && !truncated;
  return (
    <>
      <span className={styles.noteBlock} data-note-block="true">
        <span ref={ref} className={styles.note} data-note-clamped={expanded ? 'false' : 'true'} {...(inlineBubble ? { 'data-note-inline': 'true' } : {})}>
          {issue ? (
            <span className={styles.issueFlag}>
              <Icon meaning="warning" size="inline" label={t('tile.issue')} />
            </span>
          ) : null}
          {/* 🔒 Decision 7180: a rich note draws its markup through the one safe view, the notes' allowance run again on read; a plain note draws as words. */}
          {noteIsRich(text) ? <RichTextView html={text} sanitize={sanitizeNoteText} className={styles.noteRich} data-rich-text-view="note" /> : text}
        </span>
        {inlineBubble ? <span ref={setSlot} className={styles.noteBubbleSlot} data-note-bubble-slot="true" /> : null}
      </span>
      {thread ? <NoteThread {...thread} leading={more} inlineSlot={inlineBubble ? slot : null} /> : more ? <span className={styles.noteFoot} data-note-foot="true">{more}</span> : null}
    </>
  );
}

/** The Note item's shape, as a tile's list and a Deal stage-card row carry it. */
export type NoteItem = Extract<TileItem, { type: 'note' }>;

/**
 * 🔒 One note row, drawn the same wherever a notes list stands (decision 0950):
 * the author with any flag, pill or section on the left and the time in the
 * viewer's zone with the author's pencil on the right, ABOVE the text, then the
 * text capped at three lines and the replies beneath (figure 35). The Internal
 * Notes tile, a Deal stage-card row and a document page's notes panel draw
 * through it, so the three cannot drift apart again (T338, batch N3). 💰 The Deal
 * row drew its author and time beneath its text until the owner saw the two side
 * by side (product owner, 2026-09-26), and the document panel did until batch N3.
 */
export function NoteRow({ item: note }: { item: NoteItem }) {
  return (
    <li className={`${styles.item} ${styles.noteItem}`} data-tile-item={note.type} data-issue={note.issue ? 'true' : 'false'} data-note-id={note.id ?? undefined} {...(note.data ?? {})}>
      {(note.author || note.when || note.pill || note.section) && (
        <span className={styles.noteHead}>
          {/* \u{1F528} The flag, the pill and the author read as one phrase on the left; the
              pill had a line of its own, which cost a row of height on every flagged
              note (product owner, 2026-09-13). */}
          <span className={styles.noteWho}>
            {note.issue ? (
              <span className={styles.issueFlag}>
                <Icon meaning="warning" size="inline" label={t('tile.issue')} />
              </span>
            ) : null}
            {note.pill ? <span className={styles.noteTag}>{pillOf(note.pill)}</span> : null}
            {note.section ? <span className={styles.noteTag} data-note-section={note.section}>{pillOf({ text: note.section, group: 'inactive' })}</span> : null}
            <span className={styles.itemLabel}>{note.author}</span>
          </span>
          <span className={styles.noteWhen}>
            <span className={styles.itemDetail}>{note.when}</span>
            {note.onEdit ? (
              <button type="button" className={styles.rowEdit} onClick={note.onEdit} aria-label={note.editLabel ?? t('note.edit')} data-row-edit="true" {...(note.editData ?? {})}>
                <Icon meaning="edit" size="inline" />
              </button>
            ) : null}
          </span>
        </span>
      )}
      <NoteText text={note.text} issue={Boolean(!note.author && !note.when && note.issue)} thread={note.thread} />
    </li>
  );
}

/**
 * 🔒 A notes list three notes tall that scrolls inside for the rest (decision
 * 0950). The height is measured from the rows as drawn — the first row's top to
 * the fourth's — so a long note or an opened thread moves the cap with it, and a
 * list of three or fewer stands at its own height.
 *
 * 🔨 A document page's notes panel draws the same list uncapped (`capped` false):
 * it stands in the page's own flow beneath the document's blocks, not in a tile
 * beside others, so every note stands at its own height (T338, batch N3).
 */
export function NoteList({ items, className, name, capped = true }: { items: readonly NoteItem[]; className?: string | undefined; name?: string | undefined; capped?: boolean }) {
  const ref = useRef<HTMLUListElement>(null);
  const measured = useRowCap(ref, items.length, NOTE_LIST_ROWS, (el) => Array.from(el.children));
  const cap = capped ? measured : null;
  return (
    <ul ref={ref} className={`${styles.body} ${className ?? ''}`} data-flow="rows" data-note-list={name ?? 'true'} data-note-list-rows={NOTE_LIST_ROWS} data-note-list-capped={cap === null ? 'false' : 'true'} style={cap === null ? undefined : { maxHeight: cap }}>
      {items.map((item, index) => <NoteRow key={index} item={item} />)}
    </ul>
  );
}

/**
 * A list's measured cap of `rows` rows, taken again whenever a row grows or
 * shrinks: the three-note list (decision 0950) and History's five-row window
 * (decision 1404) are the one measurement. `rowsOf` names the elements that
 * stand for the rows, because a History entry is a run of grid cells with no
 * box of its own.
 */
export function useRowCap<E extends HTMLElement>(ref: { current: E | null }, count: number, rows: number, rowsOf: (el: E) => readonly Element[]): number | null {
  const [cap, setCap] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const next = noteListCap(rowsOf(el).map((row) => row.getBoundingClientRect().top), rows);
      setCap((was) => (was === next ? was : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    rowsOf(el).forEach((row) => observer.observe(row));
    return () => observer.disconnect();
    // rowsOf is a reading of the element, not a dependency: the rows are re-read on every count change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, count, rows]);
  return cap;
}


type PersonItem = Extract<TileItem, { type: 'person' }>;

/**
 * 🔒 The Person row (decision 0692, amending 0623's action column; the product
 * owner, 2026-09-24: *"on the people item make the edit right beside name on
 * right then make the call email buttons always on far right if bounding box
 * needs expanded do so"*).
 *
 * The avatar, then the name with its pill and its pencil on the name's own line,
 * the caption beneath, and the row's reach — Call, Email, Open in Maps — with its
 * one action on one line at the row's far right. 🔨 The reach never wraps under
 * the text: the text column gives up width and wraps instead, so the row grows
 * taller and the buttons hold the right edge.
 *
 * 🔨 The name is the row's link and the pencil sits beside it rather than inside
 * it, because a button inside a link is a control nobody can press on its own.
 * 🔒 A reach with more than one line carries a chevron that opens every line in
 * the row itself, beneath the buttons (T214). 🔨 It opens in the row's flow and
 * never floats: a list floated inside a tile is clipped by the tile's own scroll
 * box, which is the defect piece 3's panel was moved out of (2026-09-14).
 */
function PersonRow({ item }: { item: PersonItem }) {
  const [open, setOpen] = useState<number | null>(null);
  const listId = useId();
  const links = item.links ?? [];
  const chosen = open === null ? null : links[open] ?? null;
  const hasSide = links.length > 0 || Boolean(item.button);
  return (
    <li className={`${styles.item} ${styles.personItem} ${item.row ? styles.personRow : ''}`} data-tile-item={item.type} data-person-row={item.row ? 'true' : undefined} {...(item.data ?? {})}>
      <span className={styles.personAvatar}>
        <AttendeeAvatar person={item.person} attending={item.attending} />
      </span>
      <span className={`${styles.itemText} ${styles.personText}`}>
        <span className={styles.personName} data-person-name="true">
          {item.route ? (
            <RowLink route={item.route} className={`${styles.itemLabel} ${styles.personLink}`} title={item.person.displayName}>
              {item.person.displayName}
            </RowLink>
          ) : (
            <span className={styles.itemLabel} title={item.person.displayName}>{item.person.displayName}</span>
          )}
          {item.marker ?? null}
          {pillOf(item.pill)}
          <RowControls button={null} onEdit={item.onEdit ?? null} editLabel={item.editLabel} onDelete={item.onDelete ?? null} deleteLabel={item.deleteLabel} />
        </span>
        {item.row && item.title ? (
          <span className={styles.personTitle} title={item.title} data-person-title="true">
            {item.title}
          </span>
        ) : null}
        {item.detail ? <Detail text={item.detail} onePerLine={Boolean(item.row)} /> : null}
        {item.below ? <span className={styles.personBelow}>{item.below}</span> : null}
      </span>
      {hasSide ? (
        <span className={styles.personReach} data-person-reach="true">
          {links.map((link, i) =>
            link.choices && link.choices.length > 1 ? (
              <span key={link.href} className={`${styles.reachSplit} ${styles.reachSlot}`} data-reach-split={link.tone ?? 'plain'} data-reach-slot={link.tone === 'call' ? 'call' : 'other'}>
                <RowReach link={link} />
                <button
                  type="button"
                  className={`${styles.contactAction} ${styles.reachChevron} ${link.tone === 'call' ? styles.contactCall : ''}`}
                  onClick={() => setOpen((was) => (was === i ? null : i))}
                  aria-expanded={open === i}
                  aria-controls={open === i ? listId : undefined}
                  aria-label={t(link.tone === 'call' ? 'record.tile.chooseNumber' : 'record.tile.chooseAddress')}
                  title={t(link.tone === 'call' ? 'record.tile.chooseNumber' : 'record.tile.chooseAddress')}
                  data-reach-chooser={link.tone ?? 'plain'}
                >
                  <Icon meaning={open === i ? 'collapse' : 'expand'} size="inline" />
                </button>
              </span>
            ) : (
              <span key={link.href} className={styles.reachSlot} data-reach-slot={link.tone === 'call' ? 'call' : 'other'}>
                <RowReach link={link} />
              </span>
            ),
          )}
          {item.button ? (
            <span className={styles.reachSlot} data-reach-slot="other">
              <RowControls button={item.button} onEdit={null} />
            </span>
          ) : null}
        </span>
      ) : null}
      {chosen?.choices ? (
        <ul id={listId} className={styles.reachChoices} data-reach-choices={chosen.tone ?? 'plain'}>
          {chosen.choices.map((choice, i) => (
            <li key={`${choice.href}-${i}`}>
              {/* 🔨 Each line is a link, never a button, so the device takes it the way it takes the Call itself (decision 0623). */}
              <a className={styles.reachChoice} href={choice.href} onClick={() => setOpen(null)} data-reach-choice={i}>
                <Icon meaning={chosen.icon} size="inline" />
                <span className={styles.reachChoiceName}>{choice.name}</span>
                <span className={styles.reachChoiceValue}>{choice.value}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** One stacked person row, the item a people tile draws (decision 0692). */
export type TilePersonItem = PersonItem;

/**
 * 🔒 The stacked person row outside a tile (decision 0719): a card that lists
 * people draws them in the Contacts tile's own shape — the avatar, the name with
 * its pencil right after it, what stands under the name, and the row's one action
 * at the far right — rather than a second construction of its own. Product
 * owner, 2026-09-24: *"I would prefer the people in authority be more like the
 * contacts tile where the item in that tile is stacked."*
 */
export function PersonRowList({ items }: { items: readonly PersonItem[] }) {
  return (
    <ul className={styles.personList}>
      {items.map((item, index) => <PersonRow key={item.id ?? `${item.person.displayName}-${index}`} item={item} />)}
    </ul>
  );
}

/**
 * 🔨 A full-width row's heading is drawn once over the rows under it (piece 4): a
 * row whose heading is the one the row before it already opened draws none, so
 * three sub-skills of one skill read as one group and not as three headings
 * (product owner, 2026-09-24).
 */
function opensGroup(item: TileItem, previous: TileItem | undefined): boolean {
  if (item.type !== 'fullWidthRow' || !item.heading) return false;
  return !(previous?.type === 'fullWidthRow' && previous.heading === item.heading);
}

type TimelineItem = Extract<TileItem, { type: 'timeline' }>;

/**
 * 🔒 A History list stands five entries tall and scrolls inside for the rest
 * (T588; decision 1404, amending 0500's ten-row pager). The product owner,
 * 2026-09-29: *"Global History Tile shows 5 records then scrolls and has collapse
 * capability"*. The height is measured from the entries as drawn, the first
 * entry's top to the sixth's, the way the three-note list is (decision 0950), and
 * five or fewer stand at their own height.
 *
 * 🔨 An entry is a run of grid cells with no box of its own (`display:
 * contents`), so its date cell is what is measured.
 */
function TimelineRow({ item, windowRows }: { item: TimelineItem; windowRows?: number | undefined }) {
  const ref = useRef<HTMLOListElement>(null);
  const measured = useRowCap(ref, item.entries.length, windowRows ?? 0, (el) => Array.from(el.querySelectorAll('[data-timeline-when]')));
  const cap = windowRows ? measured : null;
  return (
    <li className={styles.item} data-tile-item={item.type}>
      {/* Five cells per entry, always: the list is one grid and an entry only contributes cells (display: contents). */}
      <ol
        ref={ref}
        className={`${styles.timeline} ${windowRows ? styles.timelineWindow : ''}`}
        {...(windowRows ? { 'data-timeline-window': String(windowRows), 'data-timeline-capped': cap === null ? 'false' : 'true' } : {})}
        style={cap === null ? undefined : { maxHeight: cap }}
      >
        {item.entries.map((e, i) => (
          <li key={i} className={styles.timelineEntry}>
            <span className={styles.timelineDot} aria-hidden="true" />
            <span className={`${styles.itemDetail} ${styles.timelineWhen}`} data-timeline-when="true">{e.when}</span>
            <span className={`${styles.itemText} ${styles.timelineText}`}>
              <span className={styles.itemLabel}>
                {e.route ? <RowLink route={e.route} className={styles.pairLink}>{e.text}</RowLink> : e.text}
              </span>
              {e.detail ? <Detail text={e.detail} /> : null}
            </span>
            <span className={styles.timelinePill}>{pillOf(e.pill)}</span>
            <span className={styles.timelineControls}>
              <RowControls button={e.button ?? null} onEdit={e.onEdit ?? null} editLabel={e.editLabel} />
            </span>
          </li>
        ))}
      </ol>
    </li>
  );
}

/**
 * 🔒 The Employments timeline (T3097; decision 7480). Every job is drawn, so the list is not one the Tile pages: it is one item and
 * carries no Show more. The dot is decoration, and the state it shows is also on the row for a test and a screen reader to read.
 */
function JobTimeline({ item }: { item: Extract<TileItem, { type: 'jobTimeline' }> }) {
  return (
    <li className={`${styles.item} ${styles.jobsItem}`} data-tile-item={item.type}>
      <ol className={styles.jobs} aria-label={t('jobTimeline.label')}>
        {item.jobs.map((job, index) => (
          <li
            key={job.key}
            className={[styles.job, job.running ? styles.jobRunning : '', index === 0 ? styles.jobFirst : '', index === item.jobs.length - 1 ? styles.jobLast : ''].filter(Boolean).join(' ')}
            data-job={job.key}
            data-job-state={job.running ? 'running' : 'ended'}
          >
            <span className={styles.jobRail} aria-hidden="true">
              <span className={styles.jobDot} data-job-dot={job.running ? 'filled' : 'hollow'} />
            </span>
            <span className={styles.jobBody}>
              <span className={styles.jobTop}>
                {job.route ? <RowLink route={job.route} className={styles.jobCompany}>{job.company}</RowLink> : <span className={styles.jobCompany}>{job.company}</span>}
                <span className={styles.jobSpan} data-job-span="true">{job.span}</span>
                {pillOf(job.pill)}
              </span>
              <ul className={styles.jobTitles}>
                {job.titles.map((title, at) => {
                  const beside = [title.extras, title.when].filter(Boolean).join(DETAIL_SEPARATOR);
                  const tail = beside ? `${DETAIL_SEPARATOR}${beside}` : null;
                  return (
                    <li key={at} data-job-title="true">
                      {title.title}
                      {tail ? <span className={styles.jobTitleMuted}>{tail}</span> : null}
                    </li>
                  );
                })}
              </ul>
            </span>
            <span className={styles.jobControls}>
              <RowControls button={job.button ?? null} onEdit={job.onEdit ?? null} editLabel={job.editLabel} onDelete={job.onDelete ?? null} deleteLabel={job.deleteLabel} />
            </span>
          </li>
        ))}
      </ol>
    </li>
  );
}

/**
 * 🔒 The item types that draw one row per entry, so a tile of them is a list the
 * Tile caps at Tile list rows (decision 5960). 🔨 Not a list: a label-value grid
 * is one block of fields, a notes list keeps its three-note window (decision
 * 0950), status pills sit side by side as Flags, and a checklist is read whole.
 */
const LIST_ROW_TYPES: ReadonlySet<TileItem['type']> = new Set<TileItem['type']>(['person', 'file', 'location', 'datedRole', 'fullWidthRow', 'timeline', 'documentRow', 'linkRow', 'contactRow', 'certificateCard']);

/** The rows an item draws: a Timeline one per entry, every other item one. */
function rowsIn(item: TileItem): number {
  return item.type === 'timeline' ? item.entries.length : 1;
}

/** The items cut to their first `count` rows, a Timeline's entries cut inside it. */
function firstRows(items: readonly TileItem[], count: number): readonly TileItem[] {
  const out: TileItem[] = [];
  let left = count;
  for (const item of items) {
    if (left <= 0) break;
    const rows = rowsIn(item);
    out.push(item.type === 'timeline' && rows > left ? { ...item, entries: item.entries.slice(0, left) } : item);
    left -= rows;
  }
  return out;
}

function renderItem(item: TileItem, index: number, resolve: StatusResolver | undefined, previous?: TileItem, windowRows?: number) {
  switch (item.type) {
    case 'statusOnly':
      return (
        <li key={index} className={`${styles.item} ${styles.statusItem}`} data-tile-item={item.type}>
          {resolve ? <StatusPill entityType={item.entityType} statusValue={item.statusValue} resolve={resolve} size={Size.Small} /> : <span>{item.statusValue}</span>}
        </li>
      );
    case 'labelValueGrid':
      return (
        <li key={index} className={styles.item} data-tile-item={item.type}>
          <dl className={styles.pairs}>
            {/* 🔒 Two owners draw two Owned By pairs (decision 7501), so a pair is keyed by its place as well as its label. */}
            {item.pairs.map((p, at) => (
              <div key={`${p.label}:${at}`} className={styles.pair}>
                <dt>{p.label}</dt>
                <dd>
                  {/* 🔨 A tile value is text the server formatted, so a negative inside it is found in that text (decision 0481). */}
                  {p.route ? <RowLink route={p.route} className={styles.pairLink}>{p.money ? <MoneyText text={p.value} /> : p.value}</RowLink> : p.money ? <MoneyText text={p.value} /> : p.value}
                  {pillOf(p.pill)}
                  <RowReach link={p.link ?? null} />
                  <RowControls button={p.button ?? null} onEdit={p.onEdit ?? null} editLabel={p.editLabel} onDelete={p.onDelete ?? null} deleteLabel={p.deleteLabel} />
                </dd>
              </div>
            ))}
          </dl>
        </li>
      );
    case 'note':
      return <NoteRow key={index} item={item} />;
    case 'person':
      return <PersonRow key={item.id ?? index} item={item} />;
    case 'file': {
      // 🔒 One File row, drawn by the Files tile and a document's Files block alike (decision 3010).
      const { type, ...file } = item;
      return <FileRow key={index} {...file} data={{ 'data-tile-item': type }} />;
    }
    case 'location':
      return (
        <li key={index} className={styles.item} data-tile-item={item.type}>
          <Linked route={item.route}>
            <Icon meaning="location" size="inline" />
            <span className={styles.itemText}>
              <span className={styles.itemLabel}>
                {item.name}
                {pillOf(item.pill)}
                {(item.badges ?? []).map((badge, at) => (
                  <TaxExemptBadge key={at} badge={badge} />
                ))}
              </span>
              <span className={styles.itemDetail}>{item.address}</span>
              {item.detail ? <Detail text={item.detail} /> : null}
            </span>
          </Linked>
          <RowReach link={item.link ?? null} />
          <RowControls button={item.button ?? null} onEdit={item.onEdit ?? null} editLabel={item.editLabel} onDelete={item.onDelete ?? null} deleteLabel={item.deleteLabel} />
        </li>
      );
    case 'fullWidthRow':
      return (
        <Fragment key={index}>
          {/* 🔨 The group's name is drawn once, over the rows under it: a sub-skill list
              says which skill it is a list of (product owner, 2026-09-20). */}
          {opensGroup(item, previous) ? (
            <li className={`${styles.item} ${styles.groupHeading}`} data-tile-group={item.heading}>
              {item.heading}
            </li>
          ) : null}
        <li className={`${styles.item} ${styles.fullWidth} ${item.muted ? styles.mutedRow : ''}`} data-tile-item={item.type} data-muted={item.muted ? 'true' : undefined}>
          {/* \u{1F528} The name and the trailing column are two groups, so a tile narrower
              than its declared minimum drops the column to a second line instead of
              squeezing the name to one word a line (product owner, 2026-09-14). */}
          <span className={styles.rowMain} data-row-main="true">
            <Linked route={item.route} onRefused={item.onRefused}>
              <span className={styles.itemLabel}>{item.text}</span>
            </Linked>
            {pillOf(item.pill)}
          </span>
          <span className={styles.rowSide} data-row-side="true">
            {item.button ? (
              <span className={styles.rowButton}>
                <Button size={Size.Small} color={Color.Inactive} text={[item.button.label]} actions={[item.button.onAct]} />
              </span>
            ) : null}
            {item.value ? <span className={styles.linkRowValue}>{item.money ? <MoneyText text={item.value} /> : item.value}</span> : null}
            {item.when ? (
              <span className={styles.rowWhen} data-row-when="true">
                <span className={styles.itemDetail}>{item.when.from}</span>
                {item.when.until ? <span className={styles.itemDetail}>{item.when.until}</span> : null}
              </span>
            ) : null}
            <RowControls button={null} onEdit={item.onEdit ?? null} editLabel={item.editLabel} onDelete={item.onDelete ?? null} deleteLabel={item.deleteLabel} />
          </span>
        </li>
        </Fragment>
      );
    /**
     * The Document Row (decision 0591): five cells per row inside one grid — the
     * date, the kind, the pill, the name with its caption beneath, and the row's
     * own controls — so the columns line up down the whole list.
     */
    case 'documentRow':
      return (
        <li key={index} className={`${styles.item} ${styles.documentRow}`} data-tile-item={item.type}>
          <span className={`${styles.itemDetail} ${styles.documentDate}`} data-document-date="true">{item.date}</span>
          <span className={`${styles.itemDetail} ${styles.documentKind}`} data-document-kind={item.kind ?? ''}>{item.kind}</span>
          <span className={styles.documentPill}>{pillOf(item.pill)}</span>
          <span className={`${styles.itemText} ${styles.documentText}`}>
            <span className={`${styles.itemLabel} ${/\s/.test(item.text.trim()) ? '' : styles.documentNumber}`} data-document-number={/\s/.test(item.text.trim()) ? undefined : 'true'}>
              {item.route ? <RowLink route={item.route} className={styles.pairLink}>{item.text}</RowLink> : item.onRefused ? <Refused onRefused={item.onRefused} className={styles.refusedText}>{item.text}</Refused> : item.text}
            </span>
            {item.detail ? <Detail text={item.detail} /> : null}
          </span>
          <span className={styles.documentControls}>
            <RowControls button={item.button ?? null} onEdit={item.onEdit ?? null} editLabel={item.editLabel} />
          </span>
        </li>
      );
    case 'certificateCard':
      return (
        <li key={index} className={`${styles.item} ${styles.certificateItem}`} data-tile-item={item.type} data-muted={item.card.muted ? 'true' : undefined}>
          <CertificateCard card={item.card} menu={item.menu} menuLabel={item.menuLabel} id={item.id ?? null} />
        </li>
      );
    case 'datedRole': {
      // 🔨 The organisation and the span read as one caption line under the role,
      // never as two stacked lines: three lines a row turned a short list of jobs
      // into a column of dates (product owner, 2026-09-14).
      const beneath = [item.organization, item.span].filter(Boolean).join(' · ');
      return (
        <li key={index} className={`${styles.item} ${styles.datedRoleItem}`} data-tile-item={item.type} {...(item.data ?? {})}>
          <Linked route={item.route}>
            <span className={styles.itemText}>
              <span className={styles.itemLabel}>{item.role}</span>
              {beneath ? <span className={styles.itemDetail} data-dated-role-beneath="true">{beneath}</span> : null}
            </span>
          </Linked>
          {pillOf(item.pill)}
          <RowReach link={item.link ?? null} />
          <RowControls button={item.button ?? null} onEdit={item.onEdit ?? null} editLabel={item.editLabel} onDelete={item.onDelete ?? null} deleteLabel={item.deleteLabel} />
        </li>
      );
    }
    case 'timeline':
      return <TimelineRow key={index} item={item} windowRows={windowRows} />;
    case 'jobTimeline':
      return <JobTimeline key={index} item={item} />;
    case 'linkRow':
      return (
        <li key={item.key ?? item.route} className={styles.item} data-tile-item={item.type} data-link-row={item.key ?? item.route}>
          <RowLink route={item.route} className={styles.linkRow}>
            <span className={styles.itemLabel}>{item.label}</span>
            <span className={styles.linkRowValue} data-uncounted={item.value === null || item.value === undefined}>
              {item.value === null || item.value === undefined ? '—' : item.money ? <MoneyText text={String(item.value)} /> : String(item.value)}
            </span>
          </RowLink>
        </li>
      );
    /**
     * 🔒 Three columns and a glyph (decision 0623): the kind's name over the job,
     * the value, the action. 🔨 The glyph is centred on the two text lines rather
     * than sitting on the first one — *"if placing the icon of the type on the far
     * left that is fine but verticaly center it to the context"* (product owner,
     * 2026-09-22).
     */
    case 'contactRow':
      return (
        <li key={index} className={`${styles.item} ${styles.contactRow}`} data-tile-item={item.type} data-contact-row={item.name}>
          <span className={styles.contactIcon} aria-hidden="true" data-contact-icon="true">
            {item.icon ? <Icon meaning={item.icon} size="inline" /> : null}
          </span>
          <span className={styles.itemText} data-contact-text="true">
            <span className={styles.itemLabel}>
              {item.name}
              {item.qualifier ? <span className={styles.contactQualifier} data-contact-qualifier="true">{item.qualifier}</span> : null}
            </span>
            {item.caption ? <span className={styles.itemDetail} data-contact-caption="true">{item.caption}</span> : null}
          </span>
          <span className={styles.contactValue} data-contact-value="true">{item.value}</span>
          <span className={styles.rowSide} data-row-side="true">
            {item.action ? (
              <a
                className={`${styles.contactAction} ${item.action.tone === 'call' ? styles.contactCall : ''}`}
                href={item.action.href}
                data-contact-action={item.action.tone}
                {...(item.action.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                <Icon meaning={item.action.tone === 'call' ? 'callNumber' : item.action.tone === 'email' ? 'mail' : 'externalLink'} size="inline" />
                {item.action.label}
              </a>
            ) : null}
            <RowControls button={null} onEdit={item.onEdit ?? null} editLabel={item.editLabel} />
          </span>
        </li>
      );
    case 'checklist':
      return (
        <li key={index} className={styles.item} data-tile-item={item.type}>
          <ul className={styles.checklist}>
            {item.entries.map((e) => (
              <li key={e.label}>
                <Checkbox label={e.label} checked={e.done} onChange={() => undefined} size={Size.Small} disabled />
              </li>
            ))}
          </ul>
        </li>
      );
    default: {
      // 🔨 A fourteenth item type that gains no case here fails to compile, rather than rendering nothing in silence.
      const missing: never = item;
      return missing;
    }
  }
}

/**
 * 🔒 A tile's own rows, drawn by a panel that reads its own route and hands the
 * Tile no items (decision 6400): the Person page's Education and Profile rows
 * are the Employments tile's dated rows, the same list, the same pencil and the
 * same remove, so a reader cannot tell the two tiles' rows apart.
 */
export function TileRowList({ items, name }: { items: readonly TileItem[]; name?: string | undefined }) {
  const resolve = useStatusResolver();
  return (
    <ul className={styles.body} data-flow="rows" data-tile-row-list={name}>
      {items.map((item, i) => renderItem(item, i, resolve, items[i - 1]))}
    </ul>
  );
}

/**
 * 🔒 A section inside a tile follows the Tile-row rule (decision 6461): its head
 * carries the section's title and its Add, and each row draws the pencil and the
 * remove through {@link RowControls}, as the Employments tile does. The head is
 * the tile header's own shape in the section's place.
 */
export function TileSectionHead({ title, children, name }: { title: string; children?: ReactNode; name?: string | undefined }) {
  return (
    <div className={styles.sectionHead} data-tile-section-head={name ?? title}>
      <span className={styles.sectionHeadTitle}>{title}</span>
      {children}
    </div>
  );
}

/** One of a section head's actions, drawn as the tile header's quiet link-shaped button. */
export function TileSectionAction({ label, onAct, disabled = false }: { label: string; onAct: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={styles.headerAction} onClick={onAct} disabled={disabled} data-tile-section-action={label}>
      {label}
    </button>
  );
}

export function Tile({ title, items, count = null, onAdd, addLabel, secondAction, addRoute, resolve, minWidth, maxWidth, onDropFiles, children, composer, aside, icon, collapsible = false, headerControl, pageSize, windowRows, empty }: TileProps) {
  const inRouter = useInRouterContext();
  // 🔒 A tile the page hands no resolver reads the shell's one, so a status item is a pill and never a bare word (decision 0639).
  const shellResolve = useStatusResolver();
  const resolveStatus = resolve ?? shellResolve;
  const [over, setOver] = useState(false);
  // The remembered choice is read once, on mount, so a page opening does not fight the person's last click.
  // 🔒 An empty tile is one slim row (decision 6700); a composer or a construction of the page's own makes it not empty.
  const slim = empty !== undefined && !composer && !children && !aside;
  const [rememberedFold, setFolded] = useState(() => (collapsible ? tileFolded(title) : false));
  // A slim row draws its words whatever fold was remembered, and the fold returns with the first row.
  const folded = slim ? false : rememberedFold;
  const fold = (next: boolean) => {
    setFolded(next);
    rememberTileFolded(title, next);
  };
  // 🔨 A page is what is DRAWN, not what arrived: the rows are already here, so showing more asks nothing.
  /**
   * 🔒 A tile that names no page size and whose items are a list draws the
   * Organization's Tile list rows (decision 5960, extending 0500). A History
   * list keeps its scrolling window (decision 1404). The count is of rows a
   * person sees, so a Timeline of ten entries is ten rows and not one item.
   */
  const listRows = useTileListRows();
  const isList = items.length > 0 && items.every((item) => LIST_ROW_TYPES.has(item.type));
  const step = pageSize !== undefined && pageSize > 0 ? pageSize : !windowRows && isList ? listRows : 0;
  const totalRows = items.reduce((sum, item) => sum + rowsIn(item), 0);
  // Pages rather than a row count are held, so a changed setting moves the page with it.
  const [pages, setPages] = useState(1);
  const paged = step > 0 && totalRows > step;
  const shownRows = paged ? Math.min(step * pages, totalRows) : totalRows;
  const visible = paged ? firstRows(items, shownRows) : items;
  const hidden = totalRows - shownRows;
  // The sentence is composed here rather than in the markup, the way every other counted line is.
  const pagerCount = [shownRows, t('tile.shownOf'), totalRows].join(' ');
  const dropProps = onDropFiles
    ? {
        onDragOver: (event: DragEvent<HTMLElement>) => {
          if (!Array.from(event.dataTransfer.types).includes('Files')) return;
          event.preventDefault();
          setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (event: DragEvent<HTMLElement>) => {
          event.preventDefault();
          setOver(false);
          const files = Array.from(event.dataTransfer.files);
          if (files.length > 0) onDropFiles(files);
        },
        'data-drop-target': 'true',
        'data-over': over ? 'true' : 'false',
      }
    : {};
  // Status-only rows sit side by side as pills, the way Flags draws them (figure 33).
  const allStatus = items.length > 0 && items.every((item) => item.type === 'statusOnly');
  /**
   * 🔨 A document list is one grid for the whole list, every row contributing its
   * five cells, so the date column is as wide as the widest date and the pills
   * stand in one column (decision 0591). A per-row grid is what misaligned the
   * Sales tile: each row's name began wherever that row's own date ended.
   */
  const documents = items.length > 0 && items.every((item) => item.type === 'documentRow');
  // People, files and places flow into columns where the tile has the width for them.
  // 🔒 Decision 11980: contacts laid one to a row share one column of acts down the tile, so they never flow into columns.
  const personRows = items.length > 0 && items.every((item) => item.type === 'person' && item.row === true);
  const flows = !personRows && items.length > 1 && items.every((item) => item.type === 'person' || item.type === 'file' || item.type === 'location');
  // 🔒 A column of people is as wide as a row whose Call and Email hold its right edge (decision 0692).
  const people = flows && items.every((item) => item.type === 'person');
  // 🔒 A column of files is as wide as a wide File row, so a tile that holds one draws Share and the pencil beside the box (decision 3013).
  const files = flows && items.every((item) => item.type === 'file');
  /**
   * 🔒 A list of notes is three notes tall and scrolls inside for the rest, the
   * same at every width (decision 0950, amending 0321 for this list): the tile
   * stands at its content's height rather than its declared block, so the cap is
   * the list's and never a block's.
   */
  const noteList = items.length > 0 && items.every((item) => item.type === 'note');
  return (
    <section className={styles.tile} style={{ maxWidth: Number.isFinite(maxWidth) ? maxWidth : undefined }} data-tile={title} data-tile-slim={slim ? 'true' : undefined} data-note-list-tile={noteList ? 'true' : undefined} data-row-window-tile={windowRows ? 'true' : undefined} data-min-width={minWidth} data-max-width={maxWidth} aria-label={title} {...dropProps}>
      <header className={secondAction ? `${styles.header} ${styles.headerTwoActions}` : styles.header}>
        {/* 🔒 The navy dot is gone from every tile header (decision 1327): it was the same mark on every tile and carried no meaning. */}
        {icon ? (
          <span className={styles.dot} data-tile-icon={icon}>
            <Icon meaning={icon} size="inline" />
          </span>
        ) : null}
        <Text as="h3" variant="small">
          {title}
          {count === null ? null : <span className={styles.count} data-tile-count={String(count)}>{count}</span>}
        </Text>
        {slim ? <span className={styles.headerEmpty} data-tile-empty={title}>{empty}</span> : null}
        {secondAction ? (
          <button type="button" className={`${styles.headerAction} ${styles.headerSecondAction}`} onClick={secondAction.onAct} data-tile-second-action={title}>
            {secondAction.label}
          </button>
        ) : null}
        {addRoute && inRouter ? (
          <Link to={addRoute} className={styles.headerAction} data-tile-action={title}>
            {addLabel ?? t('tile.add')}
          </Link>
        ) : onAdd ? (
          <button type="button" className={styles.headerAction} onClick={onAdd} data-tile-action={title}>
            {addLabel ?? t('tile.add')}
          </button>
        ) : null}
        {headerControl ? <span className={styles.headerControl} data-tile-header-control={title}>{headerControl}</span> : null}
        {/* 🔨 Folded leaves the heading and this chevron standing. A panel that vanished would read as a
            missing feature, which is the defect this plan keeps finding (decision 0500). */}
        {collapsible && !slim ? (
          <button
            type="button"
            className={styles.headerFold}
            onClick={() => fold(!folded)}
            aria-expanded={!folded}
            aria-label={t(folded ? 'tile.expand' : 'tile.collapse')}
            title={t(folded ? 'tile.expand' : 'tile.collapse')}
            data-tile-fold={folded ? 'folded' : 'open'}
          >
            <Icon meaning={folded ? 'expand' : 'collapse'} size="inline" />
          </button>
        ) : null}
      </header>
      {!folded && composer ? <div className={styles.composer} data-tile-composer={title}>{composer}</div> : null}
      {/* A construction of the page's own takes the body's place when the tile carries no items, so it fills the tile from the top (piece 20). */}
      {folded || slim ? null : children && items.length === 0 ? null : aside ? (
        <div className={styles.split}>
          <ul className={`${styles.body} ${allStatus ? styles.statusBody : ''} ${documents ? styles.documentBody : ''}`} data-flow={flows ? 'columns' : 'rows'} data-flow-people={people ? 'true' : undefined} data-flow-person-rows={personRows ? 'true' : undefined} data-flow-files={files ? 'true' : undefined}>
            {visible.map((item, i) => renderItem(item, i, resolveStatus, visible[i - 1]))}
          </ul>
          <div className={styles.aside} data-tile-aside={title}>
            {aside}
          </div>
        </div>
      ) : noteList ? (
        <NoteList items={visible as NoteItem[]} name={title} />
      ) : (
        <ul className={`${styles.body} ${allStatus ? styles.statusBody : ''} ${documents ? styles.documentBody : ''}`} data-flow={flows ? 'columns' : 'rows'} data-flow-people={people ? 'true' : undefined} data-flow-person-rows={personRows ? 'true' : undefined} data-flow-files={files ? 'true' : undefined} data-document-grid={documents ? 'true' : undefined}>
          {visible.map((item, i) => renderItem(item, i, resolveStatus, visible[i - 1], windowRows))}
        </ul>
      )}
      {/* 🔨 The count is stated rather than implied. A "Show more" with no number beside it never says
          whether one row is hidden or thirty, so nobody knows whether pressing it is worth it. */}
      {!folded && paged ? (
        <div className={styles.pager} data-tile-pager={title}>
          <span className={styles.pagerCount}>{pagerCount}</span>
          {hidden > 0 ? (
            <button type="button" className={styles.headerAction} onClick={() => setPages((was) => was + 1)} data-tile-page="more">
              {t('tile.showMore')}
            </button>
          ) : null}
          {shownRows > step ? (
            <button type="button" className={styles.headerAction} onClick={() => setPages(1)} data-tile-page="fewer">
              {t('tile.showFewer')}
            </button>
          ) : null}
        </div>
      ) : null}
      {folded ? null : children ? <div className={styles.custom} data-tile-custom={title}>{children}</div> : null}
    </section>
  );
}

/**
 * A row's own reach, drawn beside its controls in the pencil's outlined shape:
 * Open in Maps on an address, an email on an address, a call on a line. 🔨 It
 * is a link and never a button, so the device's own handler takes it — a phone
 * dials, a mail client opens a draft, and a long press offers to copy. A row
 * that names nowhere to go draws nothing.
 */
function RowReach({ link }: { link: TileRowLink | null }) {
  if (!link) return null;
  // 🔒 A worded reach draws the contact row's own action shape, so a Call reads the same on every tile (decision 0623).
  if (link.text) {
    return (
      <a
        className={`${styles.contactAction} ${link.tone === 'call' ? styles.contactCall : ''}`}
        href={link.href}
        aria-label={link.label}
        title={link.label}
        data-row-reach={link.icon}
        data-contact-action={link.tone ?? 'plain'}
        {...(link.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        <Icon meaning={link.icon} size="inline" />
        {link.text}
      </a>
    );
  }
  return (
    <a
      className={styles.rowEdit}
      href={link.href}
      aria-label={link.label}
      title={link.label}
      data-row-reach={link.icon}
      {...(link.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <Icon meaning={link.icon} size="inline" />
    </a>
  );
}

/**
 * A row's own controls, the same on every row type (decision 0309): a small
 * inactive button for the one action the row offers, such as End, and the
 * pencil where the row can be edited. A row with neither draws nothing.
 * 🔒 A link row its holder may delete carries one glyph after the pencil,
 * labelled Delete this link (T728; decision 1500): the row carries no menu, so
 * the correction stands beside the edit it is not.
 */
export function RowControls({ button, onEdit, editLabel, onDelete = null, deleteLabel }: { button: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit: (() => void) | null; editLabel?: string | undefined; onDelete?: (() => void) | null; deleteLabel?: string | undefined }) {
  return (
    <>
      {button?.icon ? (
        // A remove drawn as the tag chip's ×, not a labelled button (product owner, 2026-09-12).
        <button type="button" className={styles.rowEdit} onClick={button.onAct} aria-label={button.label} data-row-remove="true">
          <Icon meaning={button.icon} size="inline" />
        </button>
      ) : button ? (
        <span className={styles.rowButton}>
          <Button size={Size.Small} color={Color.Inactive} text={[button.label]} actions={[button.onAct]} />
        </span>
      ) : null}
      {onEdit ? (
        <button type="button" className={styles.rowEdit} onClick={onEdit} aria-label={editLabel ?? t('file.details.edit')} data-row-edit="true">
          <Icon meaning="edit" size="inline" />
        </button>
      ) : null}
      {onDelete ? (
        <button type="button" className={styles.rowEdit} onClick={onDelete} aria-label={deleteLabel ?? t('link.delete.label')} title={deleteLabel ?? t('link.delete.label')} data-row-delete-link="true">
          <Icon meaning="deleteLink" size="inline" />
        </button>
      ) : null}
    </>
  );
}
